import { useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import clsx from 'clsx'
import Coupon from '@/components/Coupon'
import NotAvailable from '@/components/NotAvailable'
import CouponLoader from '@/components/CouponLoader'
import SmartLink from '@/components/SmartLink'
import useTracking from '@/lib/tracking/useTracking'
import useLiveInfo from '@/hooks/useLiveInfo'
import useTrackingInfo from '@/hooks/useTrackingInfo'
import { Coupon as CouponType } from '@utils/types'
import styles from './index.module.sass'

interface Props {
  isLoading: boolean
  isTosAgreed: boolean
  link: string
  coupons: CouponType[]
  hasCoupons: boolean
  onRefresh: () => void
  onEmptyClick: () => void
}

const LoadingState = () => (
  <>
    <li className={styles.item}>
      <CouponLoader />
    </li>
    <li className={styles.item}>
      <CouponLoader />
    </li>
    <li className={styles.item}>
      <CouponLoader />
    </li>
  </>
)

const CouponContent = ({ link, coupons, onRefresh, onSeeMoreClick }) => {
  return (
    <>
      {
        coupons.map((coupon) => (
          <li key={coupon.policyId} className={styles.item}>
            <Coupon coupon={coupon} onRefresh={onRefresh} />
          </li>
        ))
      }
      <li>
        <SmartLink
          className={styles.seeMore}
          href={link}
          onClick={onSeeMoreClick}
        />
      </li>
    </>
  )
}

const CouponList = ({ isLoading, isTosAgreed, link, coupons = [], hasCoupons, onRefresh, onEmptyClick }: Props) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { broadcastId, shopId } = useLiveInfo()
  const { screenName } = useTrackingInfo()
  const { tracker } = useTracking()

  const onSeeMoreClick = useCallback((e) => {
    e?.preventDefault()
    if (!link) return
    router.push(link)

    tracker?.event({
      config: { utsType: 'click' },
      event: {
        screenName,
        category: 'coupon_list',
        params: {
          target: 'see_more_coupon',
          broadcast_id: broadcastId,
          shop_id: shopId
        }
      }
    })
  }, [link, screenName, broadcastId, shopId, router, tracker])

  const list = useMemo(() => coupons?.slice(0, 5), [coupons])

  if (!isLoading && !hasCoupons) return <></>

  return (
    <div
      className={clsx(
        styles.container,
        !isTosAgreed ? styles.notAvailable : undefined
      )}
      style={isLoading ? { marginBottom: '0' } : undefined}
    >
      <ul className={clsx(
        styles.list,
        isLoading ? styles.loading : undefined
      )}>
        {
          isLoading
            ? <LoadingState />
            : isTosAgreed
              ? <CouponContent
                  link={link}
                  coupons={list}
                  onRefresh={onRefresh}
                  onSeeMoreClick={onSeeMoreClick}
                />
              : <NotAvailable
                  title={ t('client.liveview.title.couponsunavailable') }
                  description={ t('client.liveview.desc.couponsunavailable') }
                  actionText={ t('client.liveview.button.opentospopup') }
                  onClick={onEmptyClick}
                />
        }
      </ul>
    </div>
  )
}

export default CouponList
